import { DragEndEvent, useDndMonitor } from "@dnd-kit/core"
import { FunctionComponent } from "react"
import { useTypedDispatch, useTypedSelector } from "../../../app/store"
import { MessageColors } from "../../simulation-messages/types"
import { addChoice } from "../../simulation/simulationSlice"
import { Event } from "../../simulation/types"
import { getMessageType } from "../../simulation/utils"
import {
  setActiveChoiceConnections,
  setAddConnectionOpenFlag,
  setSelectedTreeItem,
} from "../decisionsSlice"
import { Choice, ChoiceType } from "../types"
import { DroppableArea } from "./droppable-area"
import BaseButton from "../../../components/Button/base-button"

type AddConnectionProps = {
  choice: Choice
  additionalCancelAction?: () => void
  submitAction?: () => void
}

const AddConnection: FunctionComponent<AddConnectionProps> = (props) => {
  const dispatch = useTypedDispatch()
  const droppableID = "message-droppable-connection"

  const connections = useTypedSelector(
    (state) => state.decisionsSlice.activeChoiceConnections,
  )
  const activeChoiceType = useTypedSelector(
    (state) => state.decisionsSlice.activeChoiceType,
  )
  const selectedTreeItem = useTypedSelector(
    (state) => state.decisionsSlice.selectedTreeItem,
  )
  const messages = useTypedSelector((state) => state.simulationSlice.messages)

  useDndMonitor({
    onDragEnd({ active, over }: DragEndEvent) {
      if (over && over.id === droppableID && active?.id != null) {
        const message = messages.find(
          (message) => message.id.toString() === active.id.toString(),
        )

        if (message) {
          // Make sure message is not already in connections
          const found = connections.find((c) => c.id === message?.id)
          if (!found) {
            const newConnections = [...connections, message]
            dispatch(setActiveChoiceConnections(newConnections))
          }
        }
      }
    },
  })

  const handleRemoveConnection = (connection: Event) => {
    // filter based on id + elapsedTime
    const newConnections = connections.filter((c) => {
      return c.id !== connection.id && c.elapsedTime !== connection.elapsedTime
    })
    dispatch(setActiveChoiceConnections(newConnections))
  }

  const handleSubmitClick = () => {
    if (props.submitAction && typeof props.submitAction === "function") {
      props.submitAction()
    } else {
      if (selectedTreeItem) {
        const nextId = messages.length + 1
        const updatedSelectedTreeItem = { ...selectedTreeItem, id: nextId }
        const choice = {
          choiceType: activeChoiceType,
          item: updatedSelectedTreeItem,
          time: Date.now(),
          connections: connections,
        } as Choice
        dispatch(addChoice(choice))
      }
      dispatch(setActiveChoiceConnections([]))
      dispatch(setAddConnectionOpenFlag(false))
      dispatch(setSelectedTreeItem(null))
    }
  }

  const handleCancelClick = () => {
    dispatch(setActiveChoiceConnections([]))
    dispatch(setAddConnectionOpenFlag(false))
    dispatch(setSelectedTreeItem(null))
    if (
      props.additionalCancelAction &&
      typeof props.additionalCancelAction === "function"
    ) {
      props.additionalCancelAction()
    }
  }

  return (
    <div
      id="addConnectionsContainer"
      className="self-stretch w-full flex flex-col h-full overflow-hidden">
      {/* BUG FIX: Use explicit flex layout with proper overflow handling to keep buttons visible */}
      <div className="flex-1 flex flex-col bg-base-white text-primary-700 p-4 rounded-lg border border-gray-200 min-h-0 overflow-hidden" style={{ maxHeight: '100%' }}>
        {/* Header section - fixed at top */}
        <div
          id="addConnectionHeader"
          className="flex-shrink-0">
          <div className="self-stretch flex flex-col items-start justify-start">
            <button className="text-primary-700 bg-blue-light-200 font-semibold rounded-md py-2 px-3 mb-6">
              <div className="relative leading-[20px]">
                Add Connections
                <img
                  alt="add connection help"
                  src="/v3/assets/help-circle.svg"
                  className="relative w-4 h-4 pl-2 overflow-hidden shrink-0 cursor-help align-text-bottom"
                  title="After you make a decision or plan, you can share your thinking by selecting any messages or previous actions that led you to make that choice. Click here to add connections to a decision or plan before you execute it."
                />
              </div>
            </button>
          </div>
          <div id="action" className="pt-2">
            <div className="mb-1 font-bold">Action:</div>
            <div className="flex flex-col items-start justify-start space-y-6">
              {activeChoiceType === ChoiceType.decision &&
                props.choice.item.actionSentence}
              {activeChoiceType === ChoiceType.plan &&
                props.choice.item.planSentence}
            </div>
          </div>
          <div id="action" className="pt-6 pb-2">
            <b>Connections:</b>
          </div>
        </div>
        {/* Scrollable connections area - takes remaining space */}
        <DroppableArea
          id={droppableID}
          className="flex-1 flex flex-col overflow-y-auto min-h-0 pt-1 gap-[16px] text-sm text-gray-700">
          <div
            id="actionConnections"
            className="self-stretch flex flex-col items-start justify-start pt-1 gap-[16px] text-sm text-gray-700">
            <div id="connections" className="w-full">
              <div className="flex flex-col items-start justify-start space-y-2">
                {connections.length > 0 &&
                  connections.map((connection) => {
                    return (
                      <div
                        className="self-stretch"
                        key={`${connection.id}-${connection.elapsedTime}`}>
                        <div className="flex-1 flex flex-col items-start justify-start gap-[6px]">
                          <div className="self-stretch flex flex-row items-center justify-start gap-[8px]">
                            <div className="flex-1 relative leading-[24px] font-medium">
                              {getMessageType(connection).valueOf()}
                            </div>
                            <div className="relative text-xs leading-[18px] text-gray-600">
                              {connection.elapsedTime}
                              <img
                                alt="remove connection"
                                src="/v3/assets/trash.svg"
                                className="relative w-3 h-3 px-1 overflow-hidden cursor-pointer shrink-0"
                                title="Remove Connection"
                                onClick={() =>
                                  handleRemoveConnection(connection)
                                }
                              />
                            </div>
                          </div>
                          <div
                            className={`${
                              MessageColors[getMessageType(connection)]
                            } self-stretch rounded-tl-none rounded-tr-lg rounded-b-lg overflow-hidden flex flex-row items-center justify-start py-2.5 px-3.5 text-gray-900`}>
                            <div className="flex-1 relative leading-[24px] font-medium">
                              {connection.description}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                {connections.length === 0 && <div>No connections yet</div>}
              </div>
            </div>
          </div>
        </DroppableArea>
        {/* Submit/Cancel buttons - always visible at bottom */}
        {/* BUG FIX: Added sticky positioning as fallback for when flex layout breaks */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-white pt-4 mt-2 sticky bottom-0">
          <div className="flex flex-row items-center justify-center gap-[8px]">
            <BaseButton
              id="connectionsSubmitCancel"
              onClick={handleCancelClick}
              variant="secondary">
              Cancel
            </BaseButton>
            <BaseButton
              id="connectionsSubmit"
              onClick={handleSubmitClick}
              variant="primary">
              Submit
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddConnection
